import PropTypes from "prop-types";
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { isMobile } from "react-device-detect";
import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { ScanView } from "./scanner/scan-view";
import { DashboardView } from "./dashboard/dashboard-view";
import { WelcomeScreen } from "./welcome/welcome-screen";
import { decryptOrder } from "../services/remote-api";

export const WebScanApp = (props) => {
  const { order, appName, labId, fileFormats } = props;
  // customers not listing any file format accept the default single "stl".
  const formats = fileFormats && fileFormats.length > 0 ? fileFormats : ["stl"];
  const [decryptedOrder, setDecryptedOrder] = useState(null);
  const [welcomed, setWelcomed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // decrypt the order (for mobile devices only).
    if (!isMobile || !order || !labId) return;

    async function fetchDecryptedOrder() {
      setDecryptedOrder(null);
      setWelcomed(false);
      setLoading(true);
      setError(null);
      try {
        const json = await decryptOrder({ orderData: order, labId });
        setDecryptedOrder(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchDecryptedOrder();
  }, [order, labId]);

  // for any mobile device — phones and tablets, iOS or Android, run the
  // web-based scanner directly
  if (isMobile) {
    // Welcome screen comes first to set customer name.
    if (decryptedOrder && !welcomed) {
      return (
        <WelcomeScreen
          userName={decryptedOrder.order.webOrderUserName}
          onReady={(name) => {
            // Replace the original name to the new one.
            setDecryptedOrder((previous) => ({
              ...previous,
              order: { ...previous.order, webOrderUserName: name },
            }));
            setWelcomed(true);
          }}
        />
      );
    }

    // single-scan mode: take over the screen with the live camera view
    // (no settings screen there — upload in the customer's first format)
    if (decryptedOrder && decryptedOrder.scanMode === "single") {
      return <ScanView order={decryptedOrder} fileFormat={formats[0]} />;
    }

    // multiple-scan mode: show the scan dashboard first
    if (decryptedOrder && decryptedOrder.scanMode === "multiple") {
      return <DashboardView order={decryptedOrder} fileFormats={formats} />;
    }

    return (
      <Container maxWidth="xl" sx={{ marginTop: 3 }}>
        <Typography variant="h5" align="center">
          <strong>{appName}</strong>
        </Typography>

        {loading && (
          <Box mt={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box mt={2}>
            <Alert severity="error">Failed to load order: {error}</Alert>
          </Box>
        )}
      </Container>
    );
  }

  // for desktop (or other non-mobile) device, show a QR code of the full URL
  // (order data included) for the user to open on their mobile device's browser
  return (
    <Container maxWidth="xl" sx={{ marginTop: 3 }}>
      <Typography>
        <strong>Action needed to fulfill the order:</strong> Scan the QR code in
        your mobile device to start 3D scanning
      </Typography>
      <Box mt={8} display="flex" justifyContent="center">
        <QRCode
          style={{ width: "40%", height: "auto" }}
          value={window.location.href}
        />
      </Box>
    </Container>
  );
};

WebScanApp.propTypes = {
  order: PropTypes.string.isRequired,
  appName: PropTypes.string.isRequired,
  labId: PropTypes.string.isRequired,
  fileFormats: PropTypes.arrayOf(PropTypes.string),
};
